// src/components/CrossListKit.jsx
import React, { useMemo, useState } from "react";

const PLATFORM_META = {
  facebook: {
    label: "Facebook Marketplace",
    titleHint: "Title limit: ~65 chars",
    postUrl: "https://www.facebook.com/marketplace/create/item",
    utm: "facebook",
  },
  offerup: {
    label: "OfferUp",
    titleHint: "Title limit: ~50 chars",
    postUrl: "https://offerup.com/",
    utm: "offerup",
  },
  craigslist: {
    label: "Craigslist",
    titleHint: "Title limit: ~70 chars",
    postUrl: "https://post.craigslist.org/c/",
    utm: "craigslist",
  },
};

function safeTrim(str = "", max = 80) {
  const s = String(str || "").trim().replace(/\s+/g, " ");
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)).trimEnd() + "…";
}

function joinParts(parts) {
  return parts.filter(Boolean).join(" • ").replace(/\s+/g, " ").trim();
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function buildLinks(listingUrl, wowCheckoutUrl, platformUtm, id) {
  const listing = `${listingUrl}?utm_source=${platformUtm}&utm_medium=crosslist&utm_campaign=listing_${id}`;
  const checkoutBase = wowCheckoutUrl || listingUrl;
  const checkout = `${checkoutBase}${
    checkoutBase.includes("?") ? "&" : "?"
  }utm_source=${platformUtm}&utm_medium=crosslist&utm_campaign=listing_${id}`;
  return { listing, checkout };
}

// IMPORTANT: use "-" bullets so encoding never shows broken bullet characters
function craigslistTemplate(l, links) {
  const title = safeTrim(joinParts([l.title, l.condition, l.price]), 70);
  const desc = [
    l.title,
    "",
    "DETAILS",
    `- Price: ${l.price || "N/A"}`,
    `- Location: ${l.location || "N/A"}`,
    `- Category: ${l.category || "N/A"}`,
    `- Condition: ${l.condition || "N/A"}`,
    "",
    "DESCRIPTION",
    l.description || "",
    "",
    "HOW TO BUY",
    `Secure checkout (WOW5050): ${links.checkout}`,
    `Pickup/meetup: ${l.pickupNotes || "Arrange after checkout"}`,
    `Contact: ${l.contactMethod || "Message via WOW5050"}`,
    l.offPlatformDisclaimer
      ? "Do not pay off-platform. Use the WOW5050 secure link above."
      : null,
  ]
    .filter((x) => x !== null)
    .join("\n");
  return { title, desc };
}

function offerupTemplate(l, links) {
  const title = safeTrim(joinParts([l.title, l.price]), 50);
  const desc = [
    l.title,
    `Location: ${l.location || "N/A"}`,
    `Price: ${l.price || "N/A"}`,
    `Condition: ${l.condition || "N/A"}`,
    `Category: ${l.category || "N/A"}`,
    "",
    "- Fast, secure checkout on WOW5050",
    `- Secure checkout (WOW5050): ${links.checkout}`,
    `- Pickup/meetup: ${l.pickupNotes || "Arrange after checkout"}`,
    `- Contact: ${l.contactMethod || "Message via WOW5050"}`,
    l.offPlatformDisclaimer
      ? "- Do not pay off-platform. Use the WOW5050 secure link above."
      : null,
    "",
    l.description || "",
  ]
    .filter((x) => x !== null)
    .join("\n");
  return { title, desc };
}

function facebookTemplate(l, links) {
  const title = safeTrim(joinParts([l.title, l.price]), 65);
  const firstLine = `${l.title}${l.price ? ` — ${l.price}` : ""}`.trim();
  const desc = [
    firstLine,
    `Pickup in ${l.location || "N/A"}. Local pickup available.`,
    "",
    l.description || "",
    "",
    `Secure checkout (WOW5050): ${links.checkout}`,
    `Pickup/meetup: ${l.pickupNotes || "Arrange after checkout"}`,
    `Contact: ${l.contactMethod || "Message via WOW5050"}`,
    l.offPlatformDisclaimer
      ? "Do not pay off-platform. Use the WOW5050 secure link above."
      : null,
  ]
    .filter((x) => x !== null)
    .join("\n");
  return { title, desc };
}

function PlatformCard({
  k,
  data,
  status,
  postedUrl,
  onOpenPlatform,
  onStatusChange,
  onPostedUrlChange,
  compact = false, // compact = mobile accordion mode
}) {
  return (
    <div className="rounded-2xl border border-slate-700/70 bg-slate-950/40 p-4 max-w-full overflow-hidden min-w-0">
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <div className="text-sm font-semibold text-sky-300">{data.meta.label}</div>
          <div className="text-[11px] text-slate-400">{data.meta.titleHint}</div>
        </div>

        <div className="text-[11px] text-slate-400 min-w-0">
          Tracking link:
          <span className="block break-all max-w-full">{data.links.checkout}</span>
        </div>

        <button
          type="button"
          className="w-full sm:w-auto text-sm px-3 py-2 rounded-xl bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-500/20 transition"
          onClick={() => {
            onOpenPlatform?.(k, data.meta.postUrl);
            window.open(data.meta.postUrl, "_blank", "noopener,noreferrer");
          }}
        >
          Open posting page
        </button>
      </div>

      <div className={`grid gap-3 mt-4 ${compact ? "grid-cols-1" : "md:grid-cols-2"}`}>
        {/* Title */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3 max-w-full overflow-hidden min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-xs uppercase text-slate-400">Title</div>
            <button
              type="button"
              className="w-full sm:w-auto text-xs px-2 py-2 rounded-lg border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
              onClick={async () => {
                const ok = await copyToClipboard(data.title);
                if (ok) alert("Title copied.");
              }}
            >
              Copy title
            </button>
          </div>
          <div className="mt-2 text-sm text-slate-200 break-words max-w-full">
            {data.title}
          </div>
        </div>

        {/* Description */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-3 max-w-full overflow-hidden min-w-0">
          <div className="text-xs uppercase text-slate-400">Description</div>

          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              className="w-full text-xs px-2 py-2 rounded-lg border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
              onClick={async () => {
                const ok = await copyToClipboard(data.desc);
                if (ok) alert("Description copied.");
              }}
            >
              Copy description
            </button>

            <button
              type="button"
              className="w-full text-xs px-2 py-2 rounded-lg border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
              onClick={async () => {
                const ok = await copyToClipboard(data.links.checkout);
                if (ok) alert("Tracking checkout link copied.");
              }}
            >
              Copy tracking link
            </button>
          </div>

          <pre className="mt-2 whitespace-pre-wrap text-sm text-slate-200 break-words font-sans max-w-full">
            {data.desc}
          </pre>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <div className="md:col-span-1">
          <label className="text-xs uppercase text-slate-400">Status</label>
          <select
            value={status}
            onChange={(e) => onStatusChange?.(k, e.target.value)}
            className="mt-1 w-full rounded-lg bg-slate-800/80 border border-slate-700 p-2 outline-none focus:ring-2 focus:ring-emerald-500/40"
          >
            <option>Draft</option>
            <option>Opened</option>
            <option>Posted</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="text-xs uppercase text-slate-400">Optional: paste the posted URL</label>
          <input
            value={postedUrl}
            onChange={(e) => onPostedUrlChange?.(k, e.target.value || "")}
            placeholder="https://..."
            className="mt-1 w-full rounded-lg bg-slate-800/80 border border-slate-700 p-2 outline-none focus:ring-2 focus:ring-emerald-500/40"
          />
        </div>
      </div>
    </div>
  );
}

export default function CrossListKit({
  listing,
  selectedPlatforms,
  onOpenPlatform,
  onStatusChange,
  onPostedUrlChange,
}) {
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeMobile, setActiveMobile] = useState("craigslist");

  const computed = useMemo(() => {
    const listingUrl =
      listing?.listingUrl || `${window.location.origin}/listing/${listing?.id || ""}`;

    const out = {};
    Object.keys(PLATFORM_META).forEach((k) => {
      const meta = PLATFORM_META[k];
      const links = buildLinks(listingUrl, listing?.wowCheckoutUrl, meta.utm, listing?.id);

      const l = listing || {};
      let tpl;
      if (k === "craigslist") tpl = craigslistTemplate(l, links);
      if (k === "offerup") tpl = offerupTemplate(l, links);
      if (k === "facebook") tpl = facebookTemplate(l, links);

      out[k] = { meta, links, ...tpl };
    });

    return out;
  }, [listing]);

  const openPosting = (platformKey) => {
    const url = PLATFORM_META?.[platformKey]?.postUrl;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const platforms = (selectedPlatforms?.length
    ? selectedPlatforms
    : ["facebook", "offerup", "craigslist"]
  ).filter(Boolean);

  if (!listing?.id) return null;

  return (
    <section className="mt-6">
      <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-5 backdrop-blur max-w-full overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 min-w-0">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-emerald-300">Cross-List</h3>
            <p className="text-sm text-slate-300">
              Generate platform-ready text + links so you can post in under a minute.
            </p>

            <div className="mt-4 rounded-xl border border-cyan-500/30 bg-black/30 p-3 sm:p-4 max-w-full overflow-hidden">
              <div className="text-sm text-slate-200 font-semibold">Quick steps (1 minute)</div>
              <ol className="mt-2 list-decimal pl-5 text-sm text-slate-300 space-y-1">
                <li>
                  Fill the form above and click{" "}
                  <span className="text-slate-100 font-semibold">Submit Listing</span>.
                </li>
                <li>Scroll here, pick a platform, then copy Title + Description.</li>
                <li>Paste into the platform posting page, upload your photos, and publish.</li>
                <li className="text-slate-400">
                  Tip: Keep payments on WOW5050 using the secure checkout link.
                </li>
              </ol>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => openPosting("craigslist")}
                  className="w-full px-3 py-2 rounded-lg border border-cyan-400/50 text-slate-100 hover:bg-cyan-500/10"
                >
                  Open Craigslist
                </button>
                <button
                  type="button"
                  onClick={() => openPosting("offerup")}
                  className="w-full px-3 py-2 rounded-lg border border-cyan-400/50 text-slate-100 hover:bg-cyan-500/10"
                >
                  Open OfferUp
                </button>
                <button
                  type="button"
                  onClick={() => openPosting("facebook")}
                  className="w-full px-3 py-2 rounded-lg border border-cyan-400/50 text-slate-100 hover:bg-cyan-500/10"
                >
                  Open Facebook Marketplace
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowTemplates((v) => !v)}
                className="mt-3 text-sm text-cyan-300 underline underline-offset-4"
              >
                {showTemplates ? "Hide templates" : "Show templates (copy/paste)"}
              </button>
            </div>
          </div>

          <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              className="w-full sm:w-auto text-xs px-3 py-2 rounded-lg border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
              onClick={async () => {
                const ok = await copyToClipboard(`${window.location.origin}/listing/${listing.id}`);
                if (ok) alert("WOW listing link copied.");
              }}
            >
              Copy WOW listing link
            </button>

            <button
              type="button"
              className="w-full sm:w-auto text-xs px-3 py-2 rounded-lg border border-slate-700 hover:border-emerald-400 hover:text-emerald-200 transition"
              onClick={() => {
                (listing.photos || []).forEach((p) => {
                  if (p?.dataUrl) window.open(p.dataUrl, "_blank", "noopener,noreferrer");
                });
              }}
            >
              Open photos (new tabs)
            </button>
          </div>
        </div>

        {showTemplates && (
          <>
            {/* MOBILE MODE: accordion/tabs (only show one platform at a time) */}
            <div className="mt-4 sm:hidden">
              <div className="grid grid-cols-3 gap-2">
                {platforms.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setActiveMobile(k)}
                    className={[
                      "w-full px-2 py-2 rounded-lg border text-xs",
                      activeMobile === k
                        ? "border-emerald-400/60 text-emerald-200 bg-emerald-500/10"
                        : "border-slate-700 text-slate-200 bg-slate-900/30",
                    ].join(" ")}
                  >
                    {PLATFORM_META[k]?.label?.split(" ")[0] || k}
                  </button>
                ))}
              </div>

              <div className="mt-3">
                {platforms.map((k) => {
                  if (k !== activeMobile) return null;
                  const data = computed[k];
                  const status = listing?.crosslist?.[k]?.status || "Draft";
                  const postedUrl = listing?.crosslist?.[k]?.postedUrl || "";

                  return (
                    <PlatformCard
                      key={k}
                      k={k}
                      data={data}
                      status={status}
                      postedUrl={postedUrl}
                      onOpenPlatform={onOpenPlatform}
                      onStatusChange={onStatusChange}
                      onPostedUrlChange={onPostedUrlChange}
                      compact
                    />
                  );
                })}
              </div>
            </div>

            {/* DESKTOP MODE: normal stacked cards */}
            <div className="hidden sm:grid gap-4 mt-4 max-w-full overflow-hidden">
              {platforms.map((k) => {
                const data = computed[k];
                const status = listing?.crosslist?.[k]?.status || "Draft";
                const postedUrl = listing?.crosslist?.[k]?.postedUrl || "";

                return (
                  <PlatformCard
                    key={k}
                    k={k}
                    data={data}
                    status={status}
                    postedUrl={postedUrl}
                    onOpenPlatform={onOpenPlatform}
                    onStatusChange={onStatusChange}
                    onPostedUrlChange={onPostedUrlChange}
                  />
                );
              })}
            </div>
          </>
        )}

        <div className="mt-4 text-xs text-slate-400">
          Step A — open • Step B — paste • Step C — mark done.
        </div>
      </div>
    </section>
  );
}
